





































































import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  ref,
  watch
} from '@nuxtjs/composition-api'
import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_VIEW_NS,
  ClassifiedViewState
} from '~/store/modules/shared/classifieds/view/state'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import MyOffersModal from '~/components/shared/classified/offers/MyOffersModal.vue'
import MakeOfferModal from '~/components/shared/classified/offers/MakeOfferModal.vue'
import { ClassifiedOfferEntry, OfferState } from '~/models/classified/offers'
import CSubtitleButton from '~/components/shared/configurable/button/CSubtitleButton.vue'
import { ciMakeOffer } from '~/icons/source/regular/make-offer'
import ClassifiedOfferPrice from '~/components/car/offers/ClassifiedOfferPrice.vue'
import { capitalizeSentences } from '~/utils/string'
import CAnonGuardModal from '~/components/shared/configurable/user/CAnonGuardModal.vue'
import { useI18n } from '~/compositions/i18n'

export default defineComponent({
  components: {
    CAnonGuardModal,
    ClassifiedOfferPrice,
    CSubtitleButton,
    MakeOfferModal,
    MyOffersModal
  },
  props: {
    classifiedId: { type: Number, required: true }
  },
  setup() {
    const { state, getters } = useNamespacedStore(CLASSIFIED_VIEW_NS)
    const offerCount = computed(() => {
      return state.offerCount
    })
    const hasOfferForClassified = computed(() => {
      return state.hasOfferForClassified
    })

    const showMyOffersModal = ref(false)
    const showMakeOfferModal = ref(false)
    const showAnonGuardModal = ref(false)
    const offerToCounter = ref<ClassifiedOfferEntry | null>(null)
    const dummyOffer = ref<ClassifiedOfferEntry | null>(null)
    const { t, tc } = useI18n()

    const userIsAdminOrOwner = computed(() => {
      return getters('userIsAdminOrOwner')
    })
    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)
    const { getters: classifiedViewGetters } = useNamespacedStore<
      ClassifiedViewState
    >(CLASSIFIED_VIEW_NS)

    const isSingleOrAnon = computed(() => userGetters('isSingleOrAnon'))
    const classifiedIsOwn = computed(() => classifiedViewGetters('isOwn'))

    const myOffer = computed(() => {
      if (hasOfferForClassified.value && state.classifiedOffers.length === 1) {
        return state.classifiedOffers[0]
      }
      return null
    })

    const offersButtonClick = () => {
      if (isSingleOrAnon.value && !classifiedIsOwn.value) {
        showAnonGuardModal.value = true
      } else if (userIsAdminOrOwner.value || myOffer.value) {
        showMyOffersModal.value = true
      } else {
        showMakeOfferModal.value = true
      }
    }

    const onCounteroffer = async (offer: ClassifiedOfferEntry) => {
      offerToCounter.value = offer
      showMyOffersModal.value = false
      await nextTick()
      showMakeOfferModal.value = true
    }

    const onMakeOfferClose = () => {
      showMakeOfferModal.value = false
      offerToCounter.value = null
    }

    const loadedOffer = computed(() => {
      return myOffer.value || dummyOffer.value
    })

    const checkOfferHash = () => {
      if (!process.client) {
        return
      }
      const startsWithOffer = window.location.hash.startsWith('#offer-')
      if (
        (startsWithOffer || window.location.hash === '#offers') &&
        (offerCount.value > 0 || myOffer.value)
      ) {
        if (startsWithOffer) {
          const hashSplit = window.location.hash.split('-')
          if (hashSplit.length > 1) {
            const id = hashSplit[1]
            dummyOffer.value = { id }
          }
        }
        showMyOffersModal.value = true
      }
    }
    watch(offerCount, (val, oldVal) => {
      if (oldVal === null && val > 0) {
        // this means we just loaded for the first time
        checkOfferHash()
      }
    })
    onMounted(() => {
      checkOfferHash()
    })

    const onMyOffersModalClose = () => {
      showMyOffersModal.value = false
      dummyOffer.value = null
    }

    const buttonTitle = computed(() => {
      if (userIsAdminOrOwner.value) {
        if (offerCount.value === 0) {
          return t('waiting for offers')
        }
        return capitalizeSentences(
          tc('view offer | view offers', offerCount.value)
        )
      } else if (myOffer.value) {
        if (myOfferStateWithdrawn.value) {
          return t('make a new offer')
        }
        return t('my offer')
      } else if (userIsAdminOrOwner.value || hasOfferForClassified.value) {
        return t('offers::prosfora')
      }

      return offerCount.value === 0
        ? t('make the first offer')
        : t('make an offer')
    })

    const myOfferReceived = computed(() => {
      if (myOffer.value) {
        return !userGetters('userIds').includes(myOffer.value.latestBid.user.id)
      }
      return false
    })
    const myOfferStatePending = computed(() => {
      if (myOffer.value) {
        return myOffer.value.latestBid.state === OfferState.PENDING
      }
      return false
    })
    const myOfferStateRejected = computed(() => {
      if (myOffer.value) {
        return myOffer.value.latestBid.state === OfferState.REJECTED
      }
      return false
    })
    const myOfferStateWithdrawn = computed(() => {
      if (myOffer.value) {
        return myOffer.value.latestBid.state === OfferState.WITHDRAWN
      }
      return false
    })

    return {
      showAnonGuardModal,
      offerCount,
      ciMakeOffer,
      showMyOffersModal,
      userIsAdminOrOwner,
      offersButtonClick,
      hasOfferForClassified,
      showMakeOfferModal,
      myOffer,
      onCounteroffer,
      offerToCounter,
      onMakeOfferClose,
      checkOfferHash,
      dummyOffer,
      loadedOffer,
      onMyOffersModalClose,
      buttonTitle,
      myOfferReceived,
      myOfferStatePending,
      myOfferStateRejected,
      myOfferStateWithdrawn
    }
  }
})
